import React from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import { IBlattRow } from '../../hooks/reactQuerys/GrundbuchblattQueries';
import { PostcodeSearch } from '../../Clients/AddressTab/PostcodeSearch';
import { IPostcode } from '../../Interfaces/IPostcode';
import { RealEstateMap } from './RealEstateMap';

interface IProps {
  rows: IBlattRow[];
  setRows: (rows: IBlattRow[]) => void;
}

const rowAddressLabel = (r: IBlattRow): string => {
  const street = [r.Street, r.StreetNr].filter(Boolean).join(' ').trim();
  const place = [r.ZipCode, r.Place].filter(Boolean).join(' ').trim();
  const parcel = [
    r.Parcel ? `Flur ${r.Parcel}` : null,
    r.LandParcel ? `Flurstück ${r.LandParcel}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return [street, place].filter(Boolean).join(', ') || parcel || 'Zeile';
};

/**
 * Gekoppelter Zeilen-Editor (Blatt-zentrisch #1108): jede Zeile trägt Flur,
 * Flurstück und/oder eine Adresse. Es muss nicht alles ausgefüllt sein. Darunter
 * werden alle Zeilen mit Koordinaten auf der Karte angezeigt.
 */
export const BlattRowsEditor: React.FC<IProps> = ({ rows, setRows }) => {
  const updateRow = (idx: number, patch: Partial<IBlattRow>) =>
    setRows(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const removeRow = (idx: number) => setRows(rows.filter((_, i) => i !== idx));
  const addRow = () =>
    setRows([
      ...rows,
      {
        idRealEstateLandRegisterEntry: null,
        Parcel: '',
        LandParcel: '',
        idRealEstate: null,
        Street: '',
        StreetNr: '',
        idPostcode: null,
      },
    ]);

  const withCoords = rows.filter(
    r => r.Latitude != null && r.Longitude != null
  );
  const center = withCoords[0];
  const related = withCoords.slice(1).map(r => ({
    lat: r.Latitude as number,
    lng: r.Longitude as number,
    label: rowAddressLabel(r),
  }));

  return (
    <Box>
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1 }}>
        <Typography variant='subtitle1' fontWeight={600}>
          Flurstücke / Adressen
        </Typography>
        <Chip size='small' label={rows.length} variant='outlined' />
        <Box sx={{ flexGrow: 1 }} />
        <Button size='small' startIcon={<Add />} onClick={addRow}>
          Zeile
        </Button>
      </Stack>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
        Pro Zeile können Flur/Flurstück, eine Adresse oder beides erfasst
        werden.
      </Typography>

      {rows.length === 0 ? (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Noch keine Zeilen. Über „Zeile" hinzufügen.
        </Typography>
      ) : (
        <Table size='small' sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell width={90}>Flur</TableCell>
              <TableCell width={90}>Flurstück</TableCell>
              <TableCell>Adresse</TableCell>
              <TableCell width={48} />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r, idx) => {
              const postcodeObject: IPostcode | undefined =
                r.idPostcode && r.idPostcode > 0 && r.ZipCode && r.Place
                  ? {
                      idPostcode: r.idPostcode,
                      Postcode: r.ZipCode,
                      City: r.Place,
                      idCountry: 1,
                    }
                  : undefined;
              return (
                <TableRow key={idx} sx={{ verticalAlign: 'top' }}>
                  <TableCell>
                    <TextField
                      variant='standard'
                      value={r.Parcel ?? ''}
                      onChange={e => updateRow(idx, { Parcel: e.target.value })}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant='standard'
                      value={r.LandParcel ?? ''}
                      onChange={e =>
                        updateRow(idx, { LandParcel: e.target.value })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Stack spacing={1}>
                      <Stack direction='row' spacing={1}>
                        <TextField
                          size='small'
                          label='Straße'
                          value={r.Street ?? ''}
                          onChange={e =>
                            updateRow(idx, { Street: e.target.value })
                          }
                          sx={{ flex: 1 }}
                        />
                        <TextField
                          size='small'
                          label='Nr.'
                          value={r.StreetNr ?? ''}
                          onChange={e =>
                            updateRow(idx, { StreetNr: e.target.value })
                          }
                          sx={{ width: 90 }}
                        />
                      </Stack>
                      <PostcodeSearch
                        key={JSON.stringify(postcodeObject)}
                        postcodeObject={postcodeObject}
                        required={false}
                        updatePostcodeObject={newValue =>
                          updateRow(idx, {
                            idPostcode: newValue?.idPostcode ?? null,
                            ZipCode: newValue?.Postcode ?? null,
                            Place: newValue?.City ?? null,
                          })
                        }
                      />
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Tooltip title='Zeile entfernen'>
                      <IconButton size='small' onClick={() => removeRow(idx)}>
                        <DeleteOutline fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {center && (
        <Box>
          <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
            Lage der zugeordneten Grundstücke
          </Typography>
          <RealEstateMap
            lat={center.Latitude as number}
            lng={center.Longitude as number}
            label={rowAddressLabel(center)}
            related={related}
          />
        </Box>
      )}
    </Box>
  );
};
